// GathrXR Creator Council — founding membership tiers.
// Joining pays in-app (Stripe Embedded Checkout, components/checkout) — no
// hosted Stripe pages. The old-account Payment Links are gone from the UI;
// purchases made through them during the cut-over are still recorded by
// /api/stripe/webhook (COUNCIL_LINKS + STRIPE_COUNCIL_PAYMENT_LINKS).

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, Rocket, Gem, Check, ArrowLeft, X } from 'lucide-react';
import { supabase } from '../config/supabase';
import CheckoutModal from '../components/checkout/CheckoutModal';
import { paymentsEnabled, type ProductKey } from '../config/payments';
import { waitForPaid } from '../services/checkout';
import './LandingPage.css';

interface SeatInfo { taken: number; cap: number; }
type SeatMap = Record<string, SeatInfo>;
const TIER_LABELS: Record<string, string> = { pass: 'Founding Pass', pro: 'Creator Pro', visionary: 'Visionary Circle' };

interface Tier {
  key: string;
  icon: React.ReactNode;
  name: string;
  price: string;
  cap: string;
  months: string;
  tagline: string;
  monthly: string[];
  benefits: string[];
  featured?: boolean;
}

const TIERS: Tier[] = [
  {
    key: 'pass',
    icon: <Rocket size={22} />,
    name: 'Founding Pass',
    price: '$99',
    cap: 'Limited to 500 members',
    months: 'Monthly resources for 3 months',
    tagline: 'For early adopters, hosts, and community leaders who want in at the ground floor.',
    monthly: [
      '15 AI model generations (Meshy)',
      '25 AI skyboxes',
      '500 AI copilot build commands',
      '2 Marble AI worlds (draft) — coming soon',
    ],
    benefits: [
      'Shared cloud browser on room screens (YouTube, websites): 1,500 viewer-minutes a month',
      'Early access to everything we ship',
      'Founding-member badge & recognition',
      'Beta tools before public release',
      'Founders community & events access',
      'Passport status carried forever',
    ],
  },
  {
    key: 'pro',
    icon: <Crown size={22} />,
    name: 'Creator Pro',
    price: '$499',
    cap: 'Limited to 100 members',
    months: 'Monthly resources for 6 months',
    tagline: 'For serious creators and builders shaping worlds people return to.',
    featured: true,
    monthly: [
      '40 AI model generations (Meshy)',
      '60 AI skyboxes',
      '1,500 AI copilot build commands',
      '1 Marble AI world (high quality) + 3 drafts — coming soon',
    ],
    benefits: [
      'Everything in Founding Pass',
      'Shared cloud browser on room screens: 4,000 viewer-minutes a month',
      'First access to monetization tools',
      'Premium placement in world discovery',
      'Creator events & workshops',
      'Direct input on platform development',
    ],
  },
  {
    key: 'visionary',
    icon: <Gem size={22} />,
    name: 'Visionary Circle',
    price: '$2,500',
    cap: 'Limited to 25 members',
    months: 'Monthly resources for 9 months',
    tagline: 'The inner circle — help govern and shape what GathrXR becomes.',
    monthly: [
      '100 AI model generations (Meshy)',
      '150 AI skyboxes',
      '4,000 AI copilot build commands',
      '3 Marble AI worlds (high quality) + 10 drafts — coming soon',
    ],
    benefits: [
      'Everything in Creator Pro',
      'Shared cloud browser on room screens: 10,000 viewer-minutes a month',
      'Governance & advisory privileges',
      'VIP access to all events & launches',
      'Creator grants & opportunity pipeline',
      'Named founding recognition in-world',
      'Direct line to the founding team',
    ],
  },
];

const CouncilPage: React.FC = () => {
  const navigate = useNavigate();
  const [seats, setSeats] = useState<SeatMap | null>(null);
  const [claimed, setClaimed] = useState<{ tier: string; seat_number: number } | null>(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [buying, setBuying] = useState<ProductKey | null>(null);

  // Live seat counters. If the RPC errors (migration not applied), keep the
  // static "Limited to N members" copy — graceful degradation.
  useEffect(() => {
    supabase.rpc('get_council_seats').then(({ data, error }) => {
      if (error || !data) return;
      setSeats(data as SeatMap);
    });
  }, []);

  // After purchase: if the buyer is signed in with the checkout email, link
  // their membership and show a success banner. Graceful if RPC missing.
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (!data.session) return;
      supabase.rpc('claim_council_membership').then(({ data: row, error }) => {
        if (error || !row) return;
        setClaimed(row as { tier: string; seat_number: number });
      });
    });
  }, []);

  const claim = () => {
    supabase.rpc('claim_council_membership').then(({ data: row, error }) => {
      if (error || !row) return;
      setClaimed(row as { tier: string; seat_number: number });
      setBannerDismissed(false);
    });
    supabase.rpc('get_council_seats').then(({ data, error }) => { if (!error && data) setSeats(data as SeatMap); });
  };

  // Back from a redirect-based payment method (?checkout=cs_...).
  useEffect(() => {
    const cs = new URLSearchParams(window.location.search).get('checkout');
    if (!cs) return;
    void waitForPaid(cs).then(() => {
      claim();
      window.history.replaceState(null, '', '/council');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const join = async (tier: Tier) => {
    if (paymentsEnabled && ['pass', 'pro', 'visionary'].includes(tier.key)) {
      const { data } = await supabase.auth.getSession();
      if (!data.session) { navigate(`/login?mode=signup&next=${encodeURIComponent('/council')}`); return; }
      setBuying(tier.key as ProductKey);
      return;
    }
    // Payments switched off in this build: ask by email instead.
    window.location.href = `mailto:support@gathrxr.com?subject=${encodeURIComponent(`GathrXR Creator Council — ${tier.name}`)}&body=${encodeURIComponent(`I'd like to join the ${tier.name} (${tier.price}).`)}`;
  };

  return (
    <div className="landing-container" style={{ minHeight: '100dvh' }}>
      <div className="landing-glow" />
      <div className="landing-content" style={{ paddingBottom: 60 }}>
        <nav className="landing-nav" style={{ marginBottom: 8 }}>
          <div className="nav-logo" style={{ cursor: 'pointer' }} onClick={() => navigate('/')}>
            Gathr<span className="nav-logo-accent">XR</span>
          </div>
          <button className="nav-btn" onClick={() => navigate(-1)}>
            <ArrowLeft size={15} /> Back
          </button>
        </nav>

        <section style={{ textAlign: 'center', padding: '56px 0 16px' }}>
          <div className="hero-badge">
            <span className="hero-badge-dot" />
            Founding circle — limited seats
          </div>
          <h1 style={{ fontSize: 52, fontWeight: 800, color: 'rgba(234,246,248,0.95)', letterSpacing: '-1.5px', margin: '18px 0 14px', lineHeight: 1.05 }}>
            Creator <span className="hero-title-accent">Council</span>
          </h1>
          <p style={{ fontSize: 18, color: 'rgba(234,246,248,0.6)', maxWidth: 560, margin: '0 auto', lineHeight: 1.6 }}>
            A limited founding circle for the creators, hosts, and builders who
            shape GathrXR from the beginning — with real AI building resources
            every month, recognition that lasts, and a voice in where we go.
          </p>
        </section>

        {claimed && !bannerDismissed && (
          <div
            style={{
              display: 'flex', alignItems: 'center', gap: 12,
              maxWidth: 640, margin: '20px auto 0',
              background: 'rgba(255,209,102,0.12)',
              border: '1px solid rgba(255,209,102,0.4)',
              borderRadius: 14, padding: '14px 18px',
            }}
          >
            <Crown size={20} color="#ffd166" style={{ flexShrink: 0 }} />
            <span style={{ flex: 1, fontSize: 14, color: 'rgba(234,246,248,0.9)', fontWeight: 600 }}>
              Welcome to the Council — {TIER_LABELS[claimed.tier] || claimed.tier}, seat #{claimed.seat_number}
            </span>
            <button
              onClick={() => setBannerDismissed(true)}
              aria-label="Dismiss"
              style={{ background: 'none', border: 'none', color: 'rgba(234,246,248,0.6)', cursor: 'pointer', display: 'flex' }}
            >
              <X size={18} />
            </button>
          </div>
        )}

        <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20, marginTop: 40 }}>
          {TIERS.map((t) => {
            const seat = seats?.[t.key];
            const soldOut = !!seat && seat.taken >= seat.cap;
            return (
            <div
              key={t.key}
              style={{
                position: 'relative',
                borderRadius: 20,
                padding: '30px 26px',
                background: t.featured ? 'rgba(0,194,179,0.07)' : 'rgba(12,22,38,0.6)',
                border: `1px solid ${t.featured ? 'rgba(0,194,179,0.4)' : 'rgba(255,255,255,0.08)'}`,
                backdropFilter: 'blur(14px)',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {t.featured && (
                <div style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', background: 'var(--gx-gradient)', color: '#fff', fontSize: 11, fontWeight: 700, padding: '4px 14px', borderRadius: 999, letterSpacing: '0.06em' }}>
                  MOST POPULAR
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#00c2b3' }}>
                {t.icon}
                <h2 style={{ margin: 0, fontSize: 20, color: 'rgba(234,246,248,0.95)' }}>{t.name}</h2>
              </div>
              <div style={{ fontSize: 40, fontWeight: 800, color: '#eaf6f8', margin: '14px 0 2px' }}>
                {t.price} <span style={{ fontSize: 14, fontWeight: 500, color: 'rgba(234,246,248,0.45)' }}>one-time</span>
              </div>
              <div style={{ fontSize: 12.5, color: '#ffd166', fontWeight: 600, marginBottom: 12 }}>
                {seat
                  ? (soldOut ? 'Sold out' : `${seat.taken} of ${seat.cap} claimed`)
                  : t.cap}
              </div>
              <p style={{ fontSize: 13.5, color: 'rgba(234,246,248,0.6)', lineHeight: 1.5, minHeight: 42, margin: '0 0 16px' }}>{t.tagline}</p>

              <div style={{ background: 'rgba(0,0,0,0.25)', borderRadius: 12, padding: '14px 16px', marginBottom: 16 }}>
                <div style={{ fontSize: 11, letterSpacing: '0.08em', color: '#00c2b3', fontWeight: 700, marginBottom: 10 }}>
                  {t.months.toUpperCase()}
                </div>
                {t.monthly.map((m) => (
                  <div key={m} style={{ display: 'flex', gap: 8, fontSize: 13, color: 'rgba(234,246,248,0.85)', marginBottom: 7, alignItems: 'flex-start' }}>
                    <Check size={14} color="#00c2b3" style={{ marginTop: 2, flexShrink: 0 }} /> {m}
                  </div>
                ))}
              </div>

              <div style={{ flex: 1 }}>
                {t.benefits.map((b) => (
                  <div key={b} style={{ display: 'flex', gap: 8, fontSize: 13, color: 'rgba(234,246,248,0.65)', marginBottom: 7, alignItems: 'flex-start' }}>
                    <Check size={14} color="rgba(255,209,102,0.9)" style={{ marginTop: 2, flexShrink: 0 }} /> {b}
                  </div>
                ))}
              </div>

              <button
                onClick={() => join(t)}
                disabled={soldOut}
                style={{
                  marginTop: 20,
                  padding: '14px 0',
                  borderRadius: 999,
                  border: 'none',
                  cursor: soldOut ? 'not-allowed' : 'pointer',
                  fontSize: 15,
                  fontWeight: 700,
                  fontFamily: 'Outfit, sans-serif',
                  color: '#fff',
                  opacity: soldOut ? 0.45 : 1,
                  background: soldOut ? 'rgba(255,255,255,0.08)' : (t.featured ? 'var(--gx-gradient)' : 'rgba(255,255,255,0.1)'),
                  boxShadow: t.featured && !soldOut ? '0 12px 36px rgba(0,194,179,0.25)' : 'none',
                }}
              >
                {soldOut ? 'Sold out' : `Join ${t.name}`}
              </button>
            </div>
            );
          })}
        </section>

        <section style={{ textAlign: 'center', marginTop: 44, color: 'rgba(234,246,248,0.45)', fontSize: 13, lineHeight: 1.7, maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
          Membership is a one-time founding contribution. Monthly AI resources renew
          each month for the duration shown, then your founding status, recognition,
          and community access remain yours permanently. Unused monthly resources
          don't roll over. Caps are hard limits — when a circle fills, it's closed.
          <br /><br />
          The shared cloud browser on room screens is a member perk while your seat
          is active: each month's allowance counts every viewer's minute, and it
          renews on the 1st. Video file links (.mp4, .webm) don't use it. Share my
          screen is free for everyone, and watching any room screen is always free.
        </section>

        <footer className="landing-footer">
          <span>&copy; {new Date().getFullYear()} GathrXR. All rights reserved.</span>
          <div className="footer-links">
            <a href="/">Home</a>
            <a href="mailto:support@gathrxr.com">Contact</a>
          </div>
        </footer>
      </div>
      {buying && (
        <CheckoutModal
          product={buying}
          returnPath="/council"
          onClose={() => setBuying(null)}
          onPaid={() => { setBuying(null); claim(); }}
        />
      )}
    </div>
  );
};

export default CouncilPage;
